import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import AxiosInstance from "../../../shared/utils/axios";
import { mergeStripPlotsIntoGroup } from "./splitStripPlots";

/**
 * fetches the trial details for the given trialId
 * @param trialId
 * @returns trial details for the given trialId
 */
export const useTrial = (trialId: string | undefined): UseQueryResult<any> => {
  return useQuery({
    enabled: !!trialId,
    queryKey: ["trial", trialId],
    queryFn: async (): Promise<any> => {
      const response = await AxiosInstance.get(`/trial/${trialId as string}`);
      const loadedData = response.data;
      // Backend stores strips as N separate MultiPolygon plots; the app model
      // treats a strip design as ONE group. Merge them back on load so
      // rendering/colouring/labels/rates all see a single strip group.
      return {
        ...loadedData,
        trial_plots: mergeStripPlotsIntoGroup(
          loadedData?.trial_plots,
          loadedData?.strip_design?.is_strip === true
        ),
      };
    },
  });
};

export interface SaveTrialVariables {
  payload: any;
}

/**
 * creates a new trial with the given payload
 */
export const useSaveTrial = (): UseMutationResult<
  any,
  unknown,
  SaveTrialVariables
> => {
  return useMutation({
    mutationFn: async ({ payload }: SaveTrialVariables): Promise<any> => {
      const response = await AxiosInstance.post("/trial", payload);
      return response.data;
    },
  });
};

export interface UpdateTrialVariables {
  trialId: string;
  payload: any;
}

/**
 * patches the trial with the given trialId and invalidates its cached
 * `useTrial` query on success, so any component reading that trial picks up
 * the freshly saved data.
 */
export const useUpdateTrial = (): UseMutationResult<
  any,
  unknown,
  UpdateTrialVariables
> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      trialId,
      payload,
    }: UpdateTrialVariables): Promise<any> => {
      const response = await AxiosInstance.patch(`/trial/${trialId}`, payload);
      return response.data;
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: ["trial", variables.trialId],
      });
    },
  });
};
